/**
 * @file StudentsListSection.tsx
 */
import React, { FC } from 'react';
import { Tooltip } from 'antd';

import { Pt2 } from '../../../../components/P'; // Example placeholder
import { StudentWithProps } from '../../../../hooks/useGetStudents';
import WeekSelector from './WeekSelector';
import { ISchedule } from './types'; // Adjust as needed

const isScheduleVisibleForStudent = (
  student: StudentWithProps,
  schedule: ISchedule
): boolean => {
  if (!student.studentsSubsidyEligible) {
    return true;
  }

  if (schedule.show === undefined || schedule.show === null) {
    return true;
  }

  if (schedule.show === true || schedule.show === 1 || schedule.show === '1') {
    return true;
  }

  if (schedule.show === false || schedule.show === 0 || schedule.show === '0') {
    return false;
  }

  return true;
};

const shouldPromoteForProgram = (program: any): boolean => {
  const promotionDay = program?.promotionDay
    ? new Date(program.promotionDay)
    : new Date('2024-06-01');
  const beforePromotionDay = new Date() < promotionDay;

  if (!beforePromotionDay) {
    return false;
  }

  const referenceStartDate =
    program?.registrationWindowStartDate || program?.startDate;
  const referenceYear = new Date(referenceStartDate).getFullYear();

  if (Number.isNaN(referenceYear)) {
    return false;
  }

  const currentYear = new Date().getFullYear();
  return currentYear <= referenceYear;
};

interface IStudentsListSectionProps {
  title: string;
  tooltip: string;
  studentsArray: StudentWithProps[];
  schedules: ISchedule[];
  program: any; // or your type
  gradeMinimum?: { id: number; shortGrade: string };
  gradeMaximum?: { id: number; shortGrade: string };

  // Additional concurrency-related props you might pass to WeekSelector:
  enrolledSchedules?: any[];
  currentSelections?: any[];
  concurrencyDiscounts?: any[];
  organizationalAccount?: any;
  schedulesBilledTo?: any[];

  // Handler to gather the final selection
  onChangeSelection?: (payload: any) => void;
}

const StudentsListSection: FC<IStudentsListSectionProps> = ({
  title,
  tooltip,
  studentsArray,
  schedules,
  program,
  gradeMinimum,
  gradeMaximum,
  enrolledSchedules = [],
  currentSelections = [],
  concurrencyDiscounts = [],
  organizationalAccount,
  schedulesBilledTo,
  onChangeSelection,
}) => {
  return (
    <div style={{ marginTop: 16 }}>
      <b>
        <Tooltip title={tooltip}>{title} Students For Registration:</Tooltip>
      </b>

      {studentsArray.map((student, index) => {
        // Filter schedules based on student grade
        const eligibleSchedules = schedules.filter((schedule) => {
          if (!isScheduleVisibleForStudent(student, schedule)) {
            return false;
          }

          // Convert to numbers to ensure proper comparison
          const studentGradeId = Number(student.gradeId);

          // Keep promotion behavior aligned with Program.tsx by referencing registration window start date.
          const shouldPromote = shouldPromoteForProgram(program);

          // Get schedule grade range with promotion adjustment if needed
          let scheduleMinGrade = Number(schedule.gradeMinimum || 0);
          let scheduleMaxGrade = Number(schedule.gradeMaximum || 0);

          if (shouldPromote) {
            scheduleMinGrade -= 1;
            scheduleMaxGrade -= 1;
          }

          // Check if student grade is within the schedule's grade range
          return (
            studentGradeId >= scheduleMinGrade &&
            studentGradeId <= scheduleMaxGrade
          );
        });

        // Hide students with no visible days across all eligible schedules
        const DAYS: (keyof ISchedule)[] = [
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
        ];
        const hasAnyVisibleDay = eligibleSchedules.some((schedule) =>
          DAYS.some((day) => schedule[day])
        );
        if (!hasAnyVisibleDay) {
          return null;
        }

        // Filter currentSelections to only include the current student's selections
        const studentCurrentSelections = currentSelections
          .filter((selection) => selection.studentId === student.id)
          .map((selection) => {
            const normalizedDays = {
              monday: selection.monday ?? selection.days?.monday ?? false,
              tuesday: selection.tuesday ?? selection.days?.tuesday ?? false,
              wednesday:
                selection.wednesday ?? selection.days?.wednesday ?? false,
              thursday: selection.thursday ?? selection.days?.thursday ?? false,
              friday: selection.friday ?? selection.days?.friday ?? false,
            };

            return {
              studentId: selection.studentId,
              scheduleId: selection.scheduleId,
              program: selection.program
                ? {
                    ...selection.program,
                    programTypeId: selection.program.programTypeId,
                  }
                : undefined,
              schedule: selection.schedule,
              ...normalizedDays,
              days: normalizedDays,
            };
          });

        // Filter enrolledSchedules to only include the current student's enrollments
        const studentEnrolledSchedules = enrolledSchedules.filter(
          (enrollment) => enrollment.studentId === student.id
        );

        return (
          <Pt2 key={`student_${student.id}_${index}`}>
            <table className="w-100">
              <tbody>
                <tr>
                  <td>{student.firstName}:</td>
                  <td className="text-right">
                    <small>
                      <i>Select A Schedule</i>
                    </small>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* If enrollmentBlocked is present, display a message */}
            {student.enrollmentBlocked?.id ? (
              <span style={{ color: 'red' }}>
                Ineligible for Enrollment - Expelled
              </span>
            ) : (
              <div>
                {/* Render a WeekSelector for each available schedule */}
                {eligibleSchedules?.map((schedule) => (
                  <div data-testid={'week-selector'} key={schedule.id}>
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/* @ts-ignore - ISchedule type incompatibility between files */}
                    <WeekSelector
                      schedule={schedule}
                      program={program}
                      student={student}
                      gradeMinimum={gradeMinimum}
                      gradeMaximum={gradeMaximum}
                      // concurrency props - filtered per student
                      concurrencyDiscounts={concurrencyDiscounts}
                      currentSelections={studentCurrentSelections}
                      enrolledSchedules={studentEnrolledSchedules}
                      organizationalAccount={organizationalAccount}
                      schedulesBilledTo={schedulesBilledTo}
                      onChangeSelection={(payload) => {
                        // Forward to parent or store in a local state
                        onChangeSelection?.({
                          ...payload,
                          schedule,
                        });
                      }}
                    />
                  </div>
                ))}

                {/* Example placeholder for a component to display selected-schedule totals:
                    <SelectedScheduleTotal studentId={student.id} />
                */}
              </div>
            )}
          </Pt2>
        );
      })}

      <hr className="mt-3 mb-3" />
    </div>
  );
};

export default StudentsListSection;
